import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import EmptyState from '@app/src/components/EmptyState'
import Table from '@app/src/components/Table'
import usePaginationWithQuery from '@app/src/hooks/paginationWithQuery'
import { useGetApiQueryFilters } from '@app/src/hooks/queryFilters'
import useSort from '@app/src/hooks/sorting'
import { useQueueResponseExport } from '@app/src/hooks/useQueueResponseExport'
import { filtersWithoutTemplateIdAndPeriod } from '@app/src/pages/ResourceCollection/Filters/StatisticsConstants'
import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { Operators, RESPONSE_LATEST_SUBMITTED_FILTER } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { QuestionnaireTemplate, Response } from '@app/src/types/resourceExplorer'
import { ResourceTypes } from '@app/src/wf-constants'
import { BubbleChartOutlined } from '@mui/icons-material'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React, { useEffect, useImperativeHandle, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { dataHubAllowedFilters } from '..'
import DataHubInsightsDetailsHeader from './DataHubInsightsDetailsHeader'
import DataHubInsightsDetailsRow from './DataHubInsightsDetailsRow'

export const transformResponseFilterNames = (filter: FilterGroup[]) =>
  filter
    .filter(filter => filter.name !== 'requestItem.questionId')
    .map(filter => {
      const responseFilter = filter.name.startsWith('response')
      const requestItemTemplateFilter = filter.name.startsWith('requestItem.template')
      const questionTypeFilter = filter.name === 'requestItem.questionTypeId'

      return responseFilter
        ? { ...filter, name: filter.name.replace('response.', '') }
        : requestItemTemplateFilter
          ? { ...filter, name: filter.name.replace('requestItem.template.', 'request.template.sections.questions.') }
          : questionTypeFilter
            ? { ...filter, name: 'items.requestItem.questionTypeId' }
            : filter
    })

export type DataHubInsightsDetailsHandle = {
  triggerCustomAction: () => void
}
type DataHubInsightsDetailsProps = {
  onDataChange?: (hasData: boolean) => void
}

const DataHubInsightsDetails = React.forwardRef<DataHubInsightsDetailsHandle, DataHubInsightsDetailsProps>(
  ({ onDataChange }, ref) => {
    const { sorting, toggleSorting } = useSort()
    const filters = useGetApiQueryFilters(dataHubAllowedFilters)
    const { formatMessage } = useIntl()
    const { mutateAsync: queueExport, isPending: isExporting } = useQueueResponseExport()

    const cleanFilters = filtersWithoutTemplateIdAndPeriod(filters)
    const renamedFilters = transformResponseFilterNames(cleanFilters)
    const [page, pageSize, setPage, setPageSize] = usePaginationWithQuery({ page: 1, pageSize: 50 }, [
      filters,
      cleanFilters,
      renamedFilters,
    ])
    const templateIds = filters.find(filter => filter.name === 'response.request.questionnaireTemplateId')?.filters[0]
      .value
    const periodNames = filters.find(filter => filter.name === 'response.request.periodName')?.filters[0].value

    const isSinglePeriodSelected = Array.isArray(periodNames) ? periodNames.length === 1 : false
    const isSingleTemplateIdSelected = Array.isArray(templateIds) ? templateIds.length === 1 : false
    const isTemplateAndPeriodSelected = isSinglePeriodSelected && isSingleTemplateIdSelected
    const { productMapping } = useFlags()

    const basePayload = {
      filter: [...renamedFilters, RESPONSE_LATEST_SUBMITTED_FILTER],
      sort: sorting,
      include: [
        'responseAnalytics',
        'requestItem',
        'request',
        'request.target',
        'request.targetProduct',
        'request.template',
        'request.subscriptions.target.referralContact',
        'request.targetAliasObject',
        'request.targetAliasObject.organization.industryNaceCode',
        'request.targetAliasObject.organization.subIndustryNaceCode',
        'request.targetAliasObject.organization.country',
        'request.targetAliasObject.organization.locations',
        'request.targetAliasObject.organization.image',
        'items.requestItem.unit.symbol',
        'items.requestItem.questionType',
        'items.flagRule.creatorOrganization',
        'items.flagRule.suggestedRiskGuidance.suggestedRiskGuidanceLinks',
        'items.flagRule.suggestedRiskGuidance.suggestedRiskNaceCodes.naceCode',
        'items.scrapedAnswer.citations',
        ...(productMapping ? ['request.subscriptions.targetProduct'] : []),
      ],
      pagination: {
        pageNumber: page,
        itemsPerPage: pageSize,
      },
    }

    const {
      items: responses,
      isLoading,
      count,
      isError,
    } = useFetchCollectionWithPost<Response>({
      endpoint: endpoints.responses,
      key: FetchKey.Response,
      payload: basePayload,
      options: { enabled: isTemplateAndPeriodSelected },
    })

    const { items: allTemplates, isLoading: isAllTemplatesLoading } = useFetchCollectionWithPost<QuestionnaireTemplate>(
      {
        key: [FetchKey.RequestForAllTemplates],
        endpoint: endpoints.worldfavorAndCustomTemplates,
        payload: {
          include: ['sections.questions'],
          filter: [
            {
              name: 'id',
              filters: [
                {
                  value: templateIds,
                  operator: Operators.In,
                },
              ],
            },
          ],
        },
        options: { enabled: isSingleTemplateIdSelected },
      },
    )

    const requestItems = useMemo(() => responses?.[0]?.items?.map(item => item.requestItem), [responses])
    const requestSections = allTemplates?.[0]?.sections

    const showTargetProductColumn = useMemo(
      () => productMapping && responses.some(response => response.request.targetProduct),
      [responses, productMapping],
    )

    useEffect(() => {
      const hasData = responses && responses.length > 0
      onDataChange?.(hasData)
    }, [responses, onDataChange])

    const getExportObject = () => {
      return {
        ...basePayload,
        IsStandardExport: false,
      }
    }

    const handleExportRequest = React.useCallback(async () => {
      await queueExport({
        url: endpoints.queueDataExport(ResourceTypes.Response),
        body: getExportObject(),
      })
    }, [queueExport, getExportObject])

    useImperativeHandle(
      ref,
      () => ({
        triggerCustomAction: () => {
          if (!isExporting) {
            void handleExportRequest()
          }
        },
      }),
      [isExporting, handleExportRequest],
    )

    if (!isTemplateAndPeriodSelected || !requestSections) {
      return (
        <EmptyState
          title={formatMessage({ id: 'statistics.dataInsights.detailsTable.emptyStateTitle' })}
          description={formatMessage({ id: 'statistics.dataInsights.detailsTable.emptyStateDescription' })}
          iconComponent={BubbleChartOutlined}
        />
      )
    }

    return (
      <Table<Response>
        HeaderComponent={() => (
          <DataHubInsightsDetailsHeader
            requestItems={requestItems}
            toggleSorting={toggleSorting}
            activeSorting={sorting}
            showTargetProductColumn={showTargetProductColumn}
          />
        )}
        RowComponent={({ row }) => (
          <DataHubInsightsDetailsRow
            row={row}
            sections={requestSections}
            showTargetProductColumn={showTargetProductColumn}
          />
        )}
        count={count}
        data={responses}
        isLoading={isLoading || isAllTemplatesLoading}
        isError={isError}
        page={page}
        pageSize={pageSize}
        setPage={setPage}
        setPageSize={setPageSize}
        stickyColumnIndex={1}
      />
    )
  },
)

export default DataHubInsightsDetails
